// src/features/mech/plastics/questions/PartDetailsQuestion.jsx
import React from "react";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PerlTextField from "../../../../shared/components/PerlTextField";
import PerlSelect from "../../../../shared/components/PerlSelect";

export default function PartDetailsQuestion({ value, onChange }) {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    white: "#ffffff",
    textPrimary: "#F1F2F3",
    textSecondary: "#9097A1",
    surface: "#191A1B",
  };
  const brandColor =
    theme.custom?.perlColors?.brandColor || theme.palette?.primary?.main || "#ff6034";

  // Parse the value object or initialize with empty strings
  const detailsValue = value || {
    regrindPct: "",
    avgWallThickness: "",
    runnerType: "",
    noOfCavities: "",
  };
  const wallThickness = detailsValue.avgWallThickness;
  const wallThicknessValid =
    (typeof wallThickness === "number" || typeof wallThickness === "string") &&
    /^[+]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(String(wallThickness).trim()) &&
    Number.isFinite(Number(wallThickness)) && Number(wallThickness) > 0;

  const handleFieldChange = (field, newValue) => {
    onChange?.({
      ...detailsValue,
      [field]: newValue,
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
      <PerlTextField
        label="Regrind % (recorded only)"
        helperText="Parametric costing does not apply a regrind cost adjustment. This value is recorded only."
        inputProps={{ min: 0, max: 100, step: "any" }}
        type="number"
        value={detailsValue.regrindPct || ""}
        onChange={(e) => handleFieldChange("regrindPct", e.target.value)}
        sx={{ mt: 1 }}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      <PerlTextField
        label="Avg. Wall Thickness (mm)"
        type="number"
        required
        error={!wallThicknessValid}
        helperText={wallThicknessValid ? "Required for cycle-time calculation." : "Enter a wall thickness greater than 0 mm."}
        inputProps={{ min: 0, step: "any" }}
        value={detailsValue.avgWallThickness ?? ""}
        onChange={(e) => handleFieldChange("avgWallThickness", e.target.value)}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      <PerlSelect
        label="Runner type"
        value={detailsValue.runnerType || ""}
        onChange={(e) => handleFieldChange("runnerType", e.target.value)}
        options={["Cold runner", "Hot runner"]}
        showLabel={true}
      />
      <PerlTextField
        label="No of Cavities"
        type="number"
        value={detailsValue.noOfCavities || ""}
        onChange={(e) => handleFieldChange("noOfCavities", e.target.value)}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
    </Box>
  );
}

